import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";
import { Textarea } from "@/common/ui/textarea";
import { ApiError } from "@/lib/api-client";

import { updateRole } from "../api/organization-api";
import type { EnterpriseRole } from "../types";
import { PermissionCheckboxGroup } from "./PermissionCheckboxGroup";
import { usePermissions } from "./use-permissions";

interface EditRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: EnterpriseRole;
  onSaved: () => void;
}

export function EditRoleModal({
  open,
  onOpenChange,
  role,
  onSaved,
}: EditRoleModalProps): React.ReactElement {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>(() =>
    role.permissions.map((p) => p.id)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = usePermissions(open);

  useEffect(() => {
    if (!open) return;
    setName(role.name);
    setDescription(role.description ?? "");
    setSelectedPermissionIds(role.permissions.map((p) => p.id));
    setError("");
    setIsSubmitting(false);
  }, [open, role]);

  const togglePermission = (permissionId: string): void => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
    setError("");
  };

  const handleSubmit = async (): Promise<void> => {
    setError("");

    if (selectedPermissionIds.length === 0) {
      setError("Pick at least one permission for this role");
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedName = name.trim();
      const trimmedDescription = description.trim();

      const body: {
        name?: string;
        description?: string;
        permission_ids?: string[];
      } = {
        permission_ids: selectedPermissionIds,
      };

      if (!role.is_system) {
        if (!trimmedName) {
          setError("Role name is required");
          setIsSubmitting(false);
          return;
        }
        body.name = trimmedName;
        body.description = trimmedDescription;
      }

      await updateRole(role.id, body);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError("A role with this name already exists in this enterprise.");
        } else if (err.status === 403) {
          setError("You don't have permission to edit this role.");
        } else if (err.status === 404) {
          setError("Role no longer exists. Refresh and try again.");
        } else if (err.status === 400 || err.status === 422) {
          setError(err.message || "Please check the form and try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleCancel();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Role
            {role.is_system && <Badge variant="secondary">System</Badge>}
          </DialogTitle>
          <DialogDescription>
            {role.is_system
              ? "System roles can't be renamed or deleted, but you can adjust which permissions they grant."
              : "Update this role's name, description, or permissions."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-role-name">Name</Label>
            <Input
              id="edit-role-name"
              data-testid="edit-role-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              disabled={isSubmitting || role.is_system}
              readOnly={role.is_system}
              aria-readonly={role.is_system}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-role-description">Description</Label>
            <Textarea
              id="edit-role-description"
              data-testid="edit-role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              disabled={isSubmitting || role.is_system}
              readOnly={role.is_system}
              aria-readonly={role.is_system}
            />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            {permissionsLoading ? (
              <div
                data-testid="edit-role-permissions-loading"
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading permissions…
              </div>
            ) : permissionsError ? (
              <div
                data-testid="edit-role-permissions-error"
                className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{permissionsError}</span>
              </div>
            ) : (
              <PermissionCheckboxGroup
                permissions={permissions}
                selectedIds={selectedPermissionIds}
                onToggle={togglePermission}
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>

        {error && (
          <div
            data-testid="edit-role-error"
            className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            data-testid="edit-role-save"
            onClick={handleSubmit}
            disabled={isSubmitting || permissionsLoading}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
